import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { User, Loader2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const GenderSelector = forwardRef(({ onChangeStatus }, ref) => {
  const [selectedGender, setSelectedGender] = useState('');
  const [originalGender, setOriginalGender] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  const genderOptions = [
    { value: 'male', label: 'Male', icon: '♂️' },
    { value: 'female', label: 'Female', icon: '♀️' },
    { value: 'non-binary', label: 'X (non-binary)', icon: '⚧️' }
  ];

  // Check if there are unsaved changes
  const hasChanges = selectedGender !== originalGender;

  // Notify parent about change status
  useEffect(() => {
    if (onChangeStatus) {
      onChangeStatus(hasChanges);
    }
  }, [hasChanges, onChangeStatus]);

  useEffect(() => {
    fetchGender();
  }, []);

  const fetchGender = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/trainers/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const gender = data.gender || '';
        setSelectedGender(gender);
        setOriginalGender(gender);
      }
    } catch (error) {
      console.error('[GenderSelector] Error fetching gender:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectGender = (value) => {
    setSelectedGender(value);
  };

  const handleSave = async () => {
    if (!selectedGender) {
      toast({
        title: 'Error',
        description: 'Please select a gender',
        variant: 'destructive'
      });
      return false;
    }

    if (!hasChanges) {
      return true; // No changes to save
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/trainers/me`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ gender: selectedGender })
      });

      if (response.ok) {
        setOriginalGender(selectedGender);
        return true;
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.detail || 'Failed to update gender',
          variant: 'destructive'
        });
        return false;
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update gender',
        variant: 'destructive'
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Expose save function to parent via ref
  useImperativeHandle(ref, () => ({
    save: handleSave,
    hasChanges: () => hasChanges
  }));

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="ml-2">Loading...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="font-semibold flex items-center gap-2 text-gray-900 dark:text-white">
          <User className="w-5 h-5" />
          Gender
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Select your gender to display on your profile
        </p>
      </div>
      <div className="p-4">
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {genderOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleSelectGender(option.value)}
                className={`flex items-center justify-center gap-3 p-4 border-2 rounded-lg transition-all ${
                  selectedGender === option.value
                    ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/30'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                }`}
              >
                <span className="text-2xl">{option.icon}</span>
                <span className="font-medium text-gray-900 dark:text-white">{option.label}</span>
              </button>
            ))}
          </div>

          {!selectedGender && (
            <div className="text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg border border-amber-200 dark:border-amber-700">
              ⚠️ Please select your gender to display on your profile
            </div>
          )}

          <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {selectedGender ? (
                <span>
                  Selected: <strong className="capitalize text-gray-900 dark:text-white">{selectedGender}</strong>
                  {hasChanges && <span className="text-amber-600 dark:text-amber-400 ml-2">(unsaved)</span>}
                </span>
              ) : (
                <span>No gender selected</span>
              )}
            </div>
            <Button
              onClick={handleSave}
              disabled={!hasChanges || saving || !selectedGender}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

GenderSelector.displayName = 'GenderSelector';

export default GenderSelector;
